import { FormEvent, SyntheticEvent, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Spinner } from '../components/Spinner'
import { LegalLinks } from '../components/LegalLinks'
import { BuildStamp } from '../components/BuildStamp'
import { LoginScene } from '../components/LoginScene'
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  UserIcon,
} from '../components/icons'
import { resolveTenantId, tenantOfLogin } from '../services/tenant'
import {
  forgetLogin,
  looksLikeEmail,
  rememberLogin,
  rememberedLogin,
} from '../services/credentials'
import './LoginPage.css'
import { isStandalone, isTablet } from '../utils/install'

/**
 * The tenant slug the system administrator's account was opened under.
 *
 * Not a real institution, no row in `tenants`. The app is pointed at it once
 * the sign-in says the account is the system administrator's, so nothing a
 * previous visit remembered leaks into the panel.
 */
const SUPERADMIN_HOME_TENANT = 'yonetim'

/** The system administrator's panel. */
const SUPERADMIN_PATH = '/sistem-9f3a'

export function LoginPage() {
  const {
    login,
    switchTenant,
    isAuthenticated,
    isSuperadmin,
    loading,
    submitting,
    error,
    errorHint,
    errorCode,
    sendPasswordReset,
  } = useAuth()
  const navigate = useNavigate()
  const { tenant } = useParams()
  // Pre-filled from the last sign-in when the box below was ticked, so a
  // shared till only asks for the PIN.
  const [username, setUsername] = useState(rememberedLogin)
  const [remember, setRemember] = useState(() => rememberedLogin() !== '')
  const [pin, setPin] = useState('')
  const [pinShown, setPinShown] = useState(false)
  /** PIN kutusuna harf basıldı: sessizce yutmak yerine nedenini söyle. */
  const [pinLetters, setPinLetters] = useState(false)
  const pinRef = useRef<HTMLInputElement>(null)
  /** Son dokunulan alan ve zamanı: çift dokunuşta hepsini seçmek için. */
  const lastTap = useRef<{ id: string; at: number }>({ id: '', at: 0 })

  // Nobody types a kurum code any more. The address usually carries it
  // (/emirler/giris); on the bare domain the server works it out from the
  // identity instead, see tenantOfLogin.
  const [looking, setLooking] = useState(false)
  /** Set once a reset mail has gone out, so the row says so instead of the link. */
  const [resetSent, setResetSent] = useState(false)
  /** Whether the confirm step is open, and the address it is holding. */
  const [confirming, setConfirming] = useState(false)
  const [resetEmail, setResetEmail] = useState('')

  /** An owner types an address and a password; staff a name and a PIN. */
  const asEmail = looksLikeEmail(username)
  /**
   * PIN kutusu (yalnız rakam, rakam klavyesi) ancak bir personel adı
   * yazılmışsa. Ad kutusu boşken şifre kutusu serbest: önce şifresini yazan
   * ya da tarayıcısı adı sonradan dolduran patronun harfleri silinmesin.
   */
  const pinMode = username.trim() !== '' && !asEmail

  useEffect(() => {
    if (loading || !isAuthenticated) return
    // Already signed in as the system administrator: their place is the
    // panel, not an institution's till.
    if (isSuperadmin) {
      navigate(SUPERADMIN_PATH, { replace: true })
      return
    }
    // Reached on the bare "/giris" too, where the route has no tenant param,
    // falling back to whatever the session resolved to rather than navigating
    // to the string "undefined".
    // Ana ekrana (18 Eki 2026); kasa yetkisi olmayan oradan sipariş ekranına,
    // mutfak personeli mutfağa düşüyor.
    navigate(`/${tenant ?? resolveTenantId()}/ana`, { replace: true })
  }, [loading, isAuthenticated, isSuperadmin, navigate, tenant])

  // Nothing is focused on arrival: on a phone an auto-focused field throws the
  // keyboard up over the screen before anyone has decided to type.

  /**
   * Find the institution first, then sign in.
   *
   * The lookup runs on every sign-in, even where the address already names a
   * kurum. Identities are unique across the system, so the account knows its
   * own institution better than the address does: someone who signed out of
   * one kurum lands on "/ak/giris", and a waiter from "deneme" typing their
   * name there was refused as "sistemde tanımlı değil", the sign-in ran
   * against the kurum in the address, not theirs. Its answer has to arrive
   * before the sign-in, because the
   * membership subscription is opened against whatever tenant is current when
   * the session lands; pointing it afterwards tears the subscription down
   * mid-login and bounces the person back to an empty form.
   *
   * A kurum that cannot be found is not reported as such. Saying "no such
   * user" would turn this screen into a way to test which addresses are
   * registered, so it falls through to the same "wrong credentials" message a
   * bad password gets.
   */
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()

    let target = tenant
    /** The institution the lookup found for this identity, if any. */
    let owner: string | undefined
    setLooking(true)
    try {
      const found = await tenantOfLogin(username)
      if (found) {
        switchTenant(found)
        target = found
        owner = found
      } else if (!resolveTenantId()) {
        // No institution owns this identity and the device remembers none.
        // The system administrator is the one account that belongs to no
        // institution; the sign-in needs some tenant to run against, and
        // theirs is this one. Anyone else is refused by the sign-in itself.
        switchTenant(SUPERADMIN_HOME_TENANT)
      }
    } catch {
      // Offline, or the lookup is unavailable. Fall through: the sign-in
      // below produces the right message for a connection problem, and
      // duplicating it here would only guess at the cause.
    } finally {
      setLooking(false)
    }

    try {
      const superadmin = await login(username, pin, owner)
      // Only once the sign-in worked: remembering a name that was rejected
      // would hand the next person a wrong one to puzzle over.
      if (remember) rememberLogin(username)
      else forgetLogin()
      // The same form for everyone: the system administrator signs in here
      // with their address and password, and the account, not a hidden
      // gesture, decides where they land.
      if (superadmin) {
        switchTenant(SUPERADMIN_HOME_TENANT)
        navigate(SUPERADMIN_PATH, { replace: true })
        return
      }
      navigate(`/${owner ?? resolveTenantId() ?? target}/ana`, { replace: true })
    } catch {
      setPin('')
      pinRef.current?.focus()
    }
  }

  /**
   * Always reports success, even for an address nobody is registered under:
   * answering honestly would turn this into a way to test which addresses
   * exist. The mail simply never arrives.
   */
  /**
   * Opens the confirm step rather than sending straight away.
   *
   * The screen cannot say whether an address is registered, that would turn
   * it into a way to test which ones exist, so a typo produces the same
   * "sent" as a real address and the mail simply never arrives. Showing the
   * address back and asking for a press is the only honest way to catch the
   * typo: the person reading it knows their own address, the server may not
   * say. Prefilled from what was typed, editable because that is usually
   * where the mistake is.
   */
  function onForgot() {
    // Kullanıcı adı yazılıysa kutuya taşınmıyor: bir e-posta değil ve
    // sıfırlama postası ona gidemez.
    setResetEmail(asEmail ? username.trim() : '')
    setConfirming(true)
  }

  async function onSendReset(e: SyntheticEvent) {
    // Nested inside the sign-in form would be invalid HTML, so this is a
    // plain handler on a div; the click and the input's Enter both land here,
    // which is why the parameter is the shared base type rather than either.
    e.preventDefault()
    if (!sendPasswordReset || !resetEmail.trim()) return
    try {
      await sendPasswordReset(resetEmail)
    } catch {
      // Swallowed for the same reason.
    }
    setConfirming(false)
    setResetSent(true)
  }

  /**
   * Tek dokunuş imleci dokunulan yere koyuyor; aynı alana 350 ms içinde ikinci
   * dokunuş hepsini seçiyor (kullanıcı, 18 Eki 2026). Eskiden tek tıklama
   * hepsini seçiyordu, yazının ortasına gidilemiyordu. Çift tık olayı
   * dokunmatikte güvenilir gelmediği için süre elle ölçülüyor.
   */
  const selectOnDoubleTap = (e: ReactPointerEvent<HTMLInputElement>) => {
    const el = e.currentTarget
    const prev = lastTap.current
    if (prev.id === el.id && e.timeStamp - prev.at < 350) {
      lastTap.current = { id: '', at: 0 }
      // Tarayıcı kendi seçimini bitirdikten sonra: yoksa ikinci dokunuşun
      // sözcük seçimi bunun üstüne yazıyordu.
      requestAnimationFrame(() => el.select())
    } else {
      lastTap.current = { id: el.id, at: e.timeStamp }
    }
  }

  if (loading) {
    return <Spinner label="Yükleniyor…" full />
  }

  return (
    <div className="login">
      <aside className="login__aside login__aside--scene">
        {/* Tanıtım sitesine dönüş: giriş ekranından çıkmanın başka yolu yoktu. */}
        <a href="https://www.adissoo.com" className="login__home">
          <ArrowLeftIcon />
          Ana sayfa
        </a>
        {/* Above the name rather than beside it: this column is the brand's
            front door and has the room a nav bar does not, the mark gets to
            be a mark here instead of a bullet before a word. */}
        <a href="https://www.adissoo.com" aria-label="Adissoo ana sayfa" className="login__logo-link">
          <img src="/icon-512.png" alt="" className="login__logo" width={72} height={72} />
        </a>
        <h1 className="login__wordmark">
          {/* Cal Sans ile çizilmiş yazı (public/brand); maske, rengi sütundan alıyor. */}
          <span className="login__wordmark-img" role="img" aria-label="Adissoo" />
        </h1>
        <p className="login__tagline">Akıllı Restoran Yönetimi</p>
        <LoginScene />
      </aside>

      <div className="login__panel">
      <div className="login__welcome">
        <h2>Adissoo'ya hoş geldiniz</h2>
      </div>

      <form className="login__card" onSubmit={onSubmit} autoComplete="off">
        <div className="fld">
          <label htmlFor="u">E-posta veya kullanıcı adı</label>
          <div className="fld__with-icon">
            <span className="fld__icon" aria-hidden="true"><UserIcon /></span>
            <input
              id="u"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onPointerUp={selectOnDoubleTap}
              placeholder="cihan@gmail.com"
              autoCapitalize="none"
              spellCheck={false}
              autoComplete="username"
            />
          </div>
        </div>

        <div className="fld">
          <label htmlFor="p">{pinMode ? 'PIN' : 'Şifre'}</label>
          <div className="fld__with-toggle fld__with-icon">
            <span className="fld__icon" aria-hidden="true"><LockIcon /></span>
            <input
              id="p"
              ref={pinRef}
              // The PIN styling, wide tracking, tabular figures, is for a
              // row of digits; a password is ordinary text and reads worse in
              // it.
              className={pinMode ? 'login__pin' : undefined}
              type={pinShown ? 'text' : 'password'}
              // A staff PIN brings up the number pad on a phone; an owner's
              // password must not, or half the keyboard is missing.
              // inputMode alone is ignored by some Android browsers on a password
              // field; with the pattern beside it the number pad comes up reliably.
              inputMode={pinMode ? 'numeric' : 'text'}
              pattern={pinMode ? '[0-9]*' : undefined}
              maxLength={pinMode ? 8 : undefined}
              value={pin}
              onChange={(e) => {
                const v = e.target.value
                setPinLetters(pinMode && /\D/.test(v))
                setPin(pinMode ? v.replace(/\D/g, '') : v)
              }}
              onPointerUp={selectOnDoubleTap}
              placeholder={pinMode ? '••••••' : ''}
              autoComplete="current-password"
            />
            {/* On a shared till the PIN is typed with people waiting, and a
                mistyped one costs a whole second attempt. Not focusable by
                keyboard: Tab should go from the PIN straight to the button. */}
            <button
              type="button"
              className="fld__toggle"
              onClick={() => setPinShown((v) => !v)}
              tabIndex={-1}
              aria-label={pinShown ? 'PIN’i gizle' : 'PIN’i göster'}
            >
              {pinShown ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {pinMode && pinLetters && (
            <p className="fld__hint fld__hint--bad" role="alert">
              PIN yalnız rakamdır. E-posta ile giriyorsanız adreste @ eksik.
            </p>
          )}
        </div>

        <div className="login__row">
          {/* The name only, never the PIN. On a till two or three people share
              through the day, typing the same name every time is the part
              worth saving; the PIN is what keeps their sales apart. */}
          <label className="login__remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Beni hatırla
          </label>

          {/* Her zaman yerinde. Eskiden yalnızca kutuya e-posta yazılınca
              çıkıyordu; şifresini unutan patron onu ararken bulamıyordu, çünkü
              henüz bir şey yazmamıştı. Personel için sıfırlama kutusu PIN'i
              patronun yenilediğini söylüyor. */}
          {resetSent ? (
            <span className="login__sent">Bağlantı gönderildi</span>
          ) : (
            <button type="button" className="login__link" onClick={onForgot}>
              Şifremi unuttum
            </button>
          )}
        </div>

        {confirming && (
          <div className="reset">
            <div className="reset__head">Şifre sıfırlama bağlantısı nereye gitsin?</div>
            <p className="reset__note">
              Adresinizi kontrol edin. Bağlantı yalnızca kayıtlı bir adrese ulaşır.
              PIN ile giriyorsanız PIN'inizi işletme sahibi yeniler.
            </p>
            <input
              type="email"
              className="reset__input"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSendReset(e)
              }}
              autoComplete="email"
              inputMode="email"
              placeholder="ornek@eposta.com"
            />
            <div className="reset__actions">
              <button
                type="button"
                className="reset__cancel"
                onClick={() => setConfirming(false)}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="reset__send"
                onClick={onSendReset}
                disabled={!resetEmail.trim()}
              >
                Bağlantı gönder
              </button>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="btn btn--primary btn--lg"
          disabled={submitting || looking || !username || !pin}
        >
          {looking || submitting ? 'Giriş yapılıyor…' : 'Giriş'}
        </button>

        {/* The message says what happened, the hint says what to do, and the
            code is what gets read down a phone when neither is enough. */}
        <div className="login__err" role="alert">
          {error && (
            <>
              <div className="login__err-title">{error}</div>
              {errorHint && <div className="login__err-hint">{errorHint}</div>}
              {errorCode && <div className="login__err-code">{errorCode}</div>}
            </>
          )}
        </div>
      </form>

      {/* For an institution that has a code but no account yet. Below the
          form: every day this screen is used it is used by someone signing
          in, not setting up, but it is the one thing a new kurum is looking
          for, so it reads as a sentence rather than a bare link. */}
      <p className="login__signup">
        Hesabınız yok mu?{' '}
        <button type="button" className="login__link" onClick={() => navigate('/kayit')}>
          14 gün ücretsiz deneyin
        </button>
        {/* Kodla açılan müşteriler için, ikinci planda. */}
        <span className="login__signup-alt">
          ya da{' '}
          <button type="button" className="login__link" onClick={() => navigate('/kurulum')}>
            aktivasyon kodu ile devam et
          </button>
        </span>
      </p>

      {/* Mutfak tableti bağlama: giriş ekranında görünmüyor (kullanıcı, 11
          Eki 2026). İstisna: tablette (iPad, Android tablet) ana ekrandan
          açılan uygulama. Adres çubuğu yok, kurulum yönergesi buradan koda
          gidiyor. Telefonlarda hiç görünmüyor. */}
      {isTablet() && isStandalone() && (
      <p className="login__screen">
        <button type="button" className="login__link" onClick={() => navigate('/ekran')}>
          Mutfak veya kiosk ekranı bağla
        </button>
      </p>
      )}

      </div>

      {/* A child of the page, not of either panel: on a wide screen it sits
          at the foot of the brand column, on a narrow one at the foot of the
          page. The question "which version is this?" comes up when nobody
          can get past this screen. */}
      <div className="login__stamp">
        <LegalLinks />
        <BuildStamp />
      </div>
    </div>
  )
}
